import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";

export type WarehouseUnitType = "DVC" | "FUESNSR" | string;
export type WarehouseUnitSort = "ASC" | "DESC";

export interface WarehouseUnitNestedEntity {
  id?: string | number;
  name?: string;
  code?: string;
  enumName?: string;
  description?: string;
  [key: string]: unknown;
}

export interface WarehouseUnitLocationEntity {
  id?: string | number;
  name?: string;
  code?: string;
  number?: string | number;
  position?: string;
  [key: string]: unknown;
}

export interface WarehouseUnitRecord {
  id: string | number;
  imei?: string | null;
  serial?: string | null;
  iccid?: string | null;
  phoneNumber?: string | null;
  quantity?: string | number | null;
  active?: boolean | number | { id?: boolean | number; name?: string } | null;
  make?: WarehouseUnitNestedEntity | null;
  model?: WarehouseUnitNestedEntity | null;
  warehouse?: WarehouseUnitLocationEntity | null;
  area?: WarehouseUnitLocationEntity | null;
  lane?: WarehouseUnitLocationEntity | null;
  shelf?: WarehouseUnitLocationEntity | null;
  position?: WarehouseUnitLocationEntity | null;
  operationType?: WarehouseUnitNestedEntity | null;
  unitType?: WarehouseUnitNestedEntity | null;
  unitStatus?: WarehouseUnitNestedEntity | null;
  hardwareStatus?: WarehouseUnitNestedEntity | null;
  hwStatus?: WarehouseUnitNestedEntity | string | null;
  status?: WarehouseUnitNestedEntity | string | null;
  configurationStatus?: WarehouseUnitNestedEntity | string | boolean | null;
  client?: WarehouseUnitNestedEntity | null;
  name?: string | null;
  [key: string]: unknown;
}

export interface WarehouseUnitListParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sort?: WarehouseUnitSort;
  query?: string;
  idDistributor?: string | number;
  unitType?: WarehouseUnitType;
  [key: string]: unknown;
}

export interface WarehouseUnitListResponse {
  units?: WarehouseUnitRecord[];
  items?: WarehouseUnitRecord[];
  data?: WarehouseUnitRecord[];
  content?: WarehouseUnitRecord[];
  totalItems?: number;
  total?: number;
  totalElements?: number;
  size?: number;
  page?: number;
  currentPage?: number;
  [key: string]: unknown;
}

export const getWarehouseUnitsList = (
  params: WarehouseUnitListParams = {},
  language?: string,
) => {
  return ApiService.get<{ response: WarehouseUnitListResponse }>(
    ServicesUrlEndpoints.WAREHOUSE_UNIT.LIST,
    {
      params,
      ...getLanguageHeaders(language),
    },
  );
};

export const deleteWarehouseUnit = (
  id: string | number,
  language?: string,
) => {
  return ApiService.delete<{ response?: unknown }>(
    `${ServicesUrlEndpoints.WAREHOUSE_UNIT.LIST.replace("/list", "")}/${id}`,
    {
      ...getLanguageHeaders(language),
    },
  );
};

export interface WarehouseUnitOperationTypeLookupResponse {
  response?: WarehouseUnitNestedEntity[];
}

export const getWarehouseUnitOperationTypeLookup = (language?: string) => {
  return ApiService.get<WarehouseUnitOperationTypeLookupResponse>(
    ServicesUrlEndpoints.WAREHOUSE_UNIT.OPERATION_TYPE_LOOKUP,
    {
      ...getLanguageHeaders(language),
    },
  );
};

export interface WarehouseUnitOperationUpdatePayload {
  operationTypeId: string | number;
}

export const updateWarehouseUnitOperationType = (
  id: string | number,
  payload: WarehouseUnitOperationUpdatePayload,
  language?: string,
) => {
  return ApiService.patch<{ response?: unknown }>(
    `${ServicesUrlEndpoints.WAREHOUSE_UNIT.LIST.replace("/list", "")}/${id}`,
    payload,
    {
      ...getLanguageHeaders(language),
    },
  );
};

export interface WarehouseUnitHwStatusLookupResponse {
  response?: WarehouseUnitNestedEntity[];
}

export const getWarehouseUnitHwStatusLookup = (language?: string) => {
  return ApiService.get<WarehouseUnitHwStatusLookupResponse>(
    ServicesUrlEndpoints.WAREHOUSE_UNIT.HW_STATUS_LOOKUP,
    {
      ...getLanguageHeaders(language),
    },
  );
};

export interface WarehouseUnitCreatePayload {
  modelId: string | number;
  unitTypeId: string | number;
  warehouseId: string | number;
  positionId?: string | number;
  unitStatusId?: string | number;
  operationTypeId: string | number;
  active?: boolean;
  quantity: number;
  idDistributor?: string | number;
  imei?: string;
  serial?: string;
  phoneNumber?: string;
  notes?: string;
  length?: string;
}

export const createWarehouseUnit = (
  payload: WarehouseUnitCreatePayload,
  language?: string,
) => {
  return ApiService.post<{ response?: unknown }>(
    ServicesUrlEndpoints.WAREHOUSE_UNIT.SAVE,
    payload,
    {
      ...getLanguageHeaders(language),
      skipErrorNotification: true,
    },
  );
};

export const updateWarehouseUnit = (
  id: string | number,
  payload: Partial<WarehouseUnitCreatePayload>,
  language?: string,
) => {
  return ApiService.patch<{ response?: unknown }>(
    `${ServicesUrlEndpoints.WAREHOUSE_UNIT.LIST.replace("/list", "")}/${id}`,
    payload,
    {
      ...getLanguageHeaders(language),
    },
  );
};
